// packages/web-ui/src/state/chat.ts
//
// W2-S5 AC #9 — `chat` zustand slice.
//
// Owns the chat-panel state for one or more sessions: per-session message
// lists, streaming-message id, pending assistant id (for thinking-indicator
// gating), in-flight user message count (for regen-button disabled), plus
// session metadata (title, provider chip, capabilities). Actions are wired
// to the `postChatMessage` API helper for `sendMessage`, the sessions API
// client for `loadTranscript` / `deleteSession`, and pure local mutations
// for `appendToken` / `recordToolCall` / `recordToolResult` / `finishStream`.
//
// The slice is composed into `RootStore` via `store.ts`. Tests use the
// vanilla store factory (`createChatStore`) for isolation.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type { Result } from '@tessa-lean/base';
import type { ChatTranscript } from '@tessa-lean/base/api-schemas/index.js';
import type { ApiContext } from '../api/client.js';
import { getDefaultApiContext } from '../api/client.js';
import type { ApiError } from '../api/internal/api-call.js';
import {
  postChatMessage as defaultPostChatMessage,
  type PostChatMessageInput,
  type PostChatMessageOutput,
} from '../api/clients/chat.js';
import { sessionsApi as defaultSessionsApi, type SessionsApi } from '../api/clients/sessions.js';
import type { InlineCardEnvelope } from './inline-cards.js';
import type { RehydrateArgs } from './image-views.js';

// ─── Types ───────────────────────────────────────────────────────────────

/**
 * Tool-call entry rendered inside an assistant message bubble. Fields mirror
 * the SSE `chat:toolCall` / `chat:toolResult` payloads (Arch §3.3 line 317).
 */
export interface ToolCallEntry {
  callId: string;
  toolName: string;
  input: unknown;
  output?: unknown;
  status: 'running' | 'ok' | 'error' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

/**
 * One chat message rendered by `<MessageBubble>`. The content shape is
 * compatible with `@tessa-lean/base/api-schemas/chat.ts:ChatMessage` for
 * `role`/`content` but adds the client-only `id`, `timestamp`, `toolCalls`
 * fields needed for streaming UX.
 */
export interface ChatPanelMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: string;
  content: string;
  toolCalls?: ToolCallEntry[];
}

/**
 * Per-session capability summary persisted in session meta. `tier` is NOT
 * stored here — it's a presentation derivation owned by the chat panel (see
 * `computeCapabilityTier`), computed from the live `/api/v1/health` snapshot.
 */
export interface CapabilityChipSummary {
  hasGit: boolean;
  hasKbConfig: boolean;
  hasDomainAgent: boolean;
  activeSkillsCount: number;
}

/** Per-session header data. */
export interface SessionMeta {
  id: string;
  title: string;
  providerName: string;
  tokensUsed: number | null;
  capabilities: CapabilityChipSummary;
  lastModified: string;
}

/** Mention reference forwarded with `sendMessage`. */
export interface ChipMention {
  chipText: string;
  entityId: string;
}

// ─── State ───────────────────────────────────────────────────────────────

export interface ChatSliceState {
  /** Flat list of session metadata (ordered newest-first). */
  sessions: SessionMeta[];
  activeSessionId: string | null;
  messagesById: Record<string, ChatPanelMessage[]>;
  streamingMessageIdBySession: Record<string, string | null>;
  pendingAssistantMessageIdBySession: Record<string, string | null>;
  /**
   * Wall-clock timestamp of the moment `sendMessage` did the optimistic
   * update for the in-flight assistant turn (per session). The
   * `useThinkingIndicatorVisible` hook uses this to decide whether the
   * stored `lastTokenAtBySession[sid]` belongs to THIS turn or to a
   * previous one — without it, the indicator stays hidden on follow-up
   * turns because the previous turn's `lastTokenAt` is non-null and
   * Phase A (`lastToken === null`) never re-fires.
   */
  pendingStartedAtBySession: Record<string, number>;
  lastTokenAtBySession: Record<string, number>;
  lastToolCallAtBySession: Record<string, number>;
  lastToolResultAtBySession: Record<string, number>;
  inFlightUserMessageCountBySession: Record<string, number>;
}

export const CHAT_INITIAL_STATE: ChatSliceState = {
  sessions: [],
  activeSessionId: null,
  messagesById: {},
  streamingMessageIdBySession: {},
  pendingAssistantMessageIdBySession: {},
  pendingStartedAtBySession: {},
  lastTokenAtBySession: {},
  lastToolCallAtBySession: {},
  lastToolResultAtBySession: {},
  inFlightUserMessageCountBySession: {},
};

// ─── Action shapes ───────────────────────────────────────────────────────

export interface SendMessageResult {
  messageId: string;
}

export interface ChatSliceActions {
  setActiveSession: (sessionId: string | null) => void;
  loadTranscript: (sessionId: string) => Promise<Result<void, ApiError>>;
  sendMessage: (
    sessionId: string,
    body: string,
    mentions?: ChipMention[],
    /**
     * Optional send options. `layoutAdjust` marks the message as an explicit
     * layout-adjust submit from a view card's inline "Adjust layout" input,
     * carrying the exact target viewId — the server then routes it straight
     * to the layout compiler for that view instead of re-judging the text
     * with the chat layout-vs-question heuristic (live-found fix: "move
     * coolant pump up" used to fall through to the KB orchestrator).
     */
    opts?: { layoutAdjust?: { viewId: string } },
  ) => Promise<Result<SendMessageResult, ApiError>>;
  appendToken: (input: { messageId: string; text: string; sessionId?: string }) => void;
  recordToolCall: (input: {
    messageId: string;
    toolName: string;
    input: unknown;
    callId: string;
    sessionId?: string;
  }) => void;
  recordToolResult: (input: {
    callId: string;
    output?: unknown;
    status: 'ok' | 'error' | 'cancelled';
    errorMessage?: string;
  }) => void;
  appendSystemMessage: (sessionId: string, content: string) => void;
  /**
   * W2-S6-integration alias for `appendSystemMessage`. Exists so call sites
   * that semantically post a "system notice" (e.g. CLAUDE.md "Response
   * Views" pipeline step 5 — `"You discarded the generated view."`) read
   * intentionally rather than reusing `appendSystemMessage` for both
   * streaming-control system messages and notice text. Default impl just
   * forwards to `appendSystemMessage` on the active session id; if no
   * session is active the call is a console.warn no-op so SSE replay during
   * session bootstrap doesn't crash.
   */
  appendSystemNotice: (content: string) => void;
  /**
   * W2-S6-integration — attach an inline-card envelope to an assistant
   * message. Default impl is a no-op + console.warn (the chat slice can
   * be created standalone for tests, in which case there is no inline-cards
   * slice available). The root-store composition in `state/store.ts`
   * overrides this with a delegate that calls
   * `inlineCardsSlice.attachCard(messageId, envelope)`.
   *
   * Returns the canonical card index (existing or newly-appended) for
   * idempotent SSE replay, mirroring `inlineCardsSlice.attachCard`.
   */
  attachCardToMessage: (messageId: string, envelope: InlineCardEnvelope) => number;
  finishStream: (sessionId: string) => void;
  setSessionTitle: (sessionId: string, title: string) => void;
  setSessionMeta: (sessionId: string, partial: Partial<Omit<SessionMeta, 'id'>>) => void;
  regenerateLast: (sessionId: string) => Promise<void>;
  clearMessages: (sessionId: string) => Promise<void>;
  /**
   * Splice a single message out of the active session. The caller passes
   * the client-side message id; the slice locates its index in the local
   * array, sends the DELETE to the server, and optimistically removes it
   * from local state on success.
   */
  deleteMessage: (sessionId: string, messageId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<Result<void, ApiError>>;
  exportTranscript: (sessionId: string, format: 'json' | 'markdown') => string;
  retryToolCall: (callId: string) => void;
  rerunToolCall: (callId: string) => void;
  /** Test/debug helper. */
  _resetChat: () => void;
}

export type ChatSlice = ChatSliceState & ChatSliceActions;

// ─── Configuration ──────────────────────────────────────────────────────

export interface ChatSliceConfig {
  postChatMessage?: (
    body: PostChatMessageInput,
    ctx: ApiContext,
  ) => Promise<Result<PostChatMessageOutput, ApiError>>;
  sessionsApi?: SessionsApi;
  apiContext?: ApiContext;
  /** Override `Date.now` for deterministic tests. */
  now?: () => number;
  /** Override id generator for deterministic tests. */
  generateId?: () => string;
  /**
   * W2-S6-integration — delegate that forwards `attachCardToMessage` to
   * the composed inline-cards slice (`createInlineCardsSlice`). The
   * root-store composition wires this so the chat slice can attach cards
   * without importing the inline-cards store directly (avoiding cyclic
   * runtime deps and preserving testability of `createChatStore`).
   *
   * Returns the canonical card index per the inline-cards slice contract.
   */
  attachInlineCardDelegate?: (
    messageId: string,
    envelope: InlineCardEnvelope,
  ) => number;
  /**
   * IV3-CARD — delegate that forwards a persisted image record into the
   * composed image-views slice's `rehydrateFromManifest` action. The
   * root-store composition (`state/store.ts`) wires this so `loadTranscript`
   * can re-seed an LLM-raster ("illustrative-image") card on the producing
   * turn without the chat slice importing the image-views store directly
   * (avoiding cyclic runtime deps and preserving `createChatStore`
   * testability). When undefined (standalone test path) the rehydrate is a
   * no-op + console.warn.
   */
  rehydrateImageFromManifest?: (args: RehydrateArgs) => void;
}

function defaultGenerateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // jsdom fallback (older versions). Not cryptographically strong — only used
  // in tests where deterministic ids are usually injected anyway.
  return `id-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function emptyCapabilities(): CapabilityChipSummary {
  return {
    hasGit: false,
    hasKbConfig: false,
    hasDomainAgent: false,
    activeSkillsCount: 0,
  };
}

// ─── Slice creator ──────────────────────────────────────────────────────

export const createChatSlice =
  (config: ChatSliceConfig = {}): StateCreator<ChatSlice> =>
  (set, get) => {
    const postChat = config.postChatMessage ?? defaultPostChatMessage;
    const sessionsApi = config.sessionsApi ?? defaultSessionsApi;
    const ctx = config.apiContext ?? getDefaultApiContext();
    const now = config.now ?? (() => Date.now());
    const genId = config.generateId ?? defaultGenerateId;
    const attachInlineCardDelegate = config.attachInlineCardDelegate;
    const rehydrateImageFromManifest = config.rehydrateImageFromManifest;

    // Helper: locate an assistant message across sessions and return
    // [sessionId, message-index]. Returns null when not found.
    function findMessageById(messageId: string): [string, number] | null {
      const map = get().messagesById;
      for (const [sessionId, list] of Object.entries(map)) {
        const idx = list.findIndex((m) => m.id === messageId);
        if (idx >= 0) return [sessionId, idx];
      }
      return null;
    }

    // Helper: locate a tool-call entry by callId across all sessions.
    function findCallById(
      callId: string,
    ): { sessionId: string; messageIdx: number; callIdx: number } | null {
      const map = get().messagesById;
      for (const [sessionId, list] of Object.entries(map)) {
        for (let m = 0; m < list.length; m++) {
          const calls = list[m]?.toolCalls;
          if (calls === undefined) continue;
          for (let c = 0; c < calls.length; c++) {
            if (calls[c]?.callId === callId) {
              return { sessionId, messageIdx: m, callIdx: c };
            }
          }
        }
      }
      return null;
    }

    // Helper: ensure the assistant message exists; create with given id if not.
    function ensureAssistantMessage(sessionId: string, messageId: string): void {
      const list = get().messagesById[sessionId] ?? [];
      if (list.some((m) => m.id === messageId)) return;
      const newMsg: ChatPanelMessage = {
        id: messageId,
        role: 'assistant',
        timestamp: new Date(now()).toISOString(),
        content: '',
        toolCalls: [],
      };
      set((s) => ({
        ...s,
        messagesById: {
          ...s.messagesById,
          [sessionId]: [...(s.messagesById[sessionId] ?? []), newMsg],
        },
        streamingMessageIdBySession: {
          ...s.streamingMessageIdBySession,
          [sessionId]: messageId,
        },
      }));
    }

    return {
      ...CHAT_INITIAL_STATE,

      setActiveSession: (sessionId) => {
        set((s) => ({ ...s, activeSessionId: sessionId }));
      },

      loadTranscript: async (sessionId) => {
        const res = await sessionsApi.getTranscript(ctx, sessionId);
        if (!res.ok) return res;
        const transcript: ChatTranscript = res.value;
        // Race guard: if a message landed (optimistic send / SSE token) while
        // the fetch was in flight, don't clobber it.
        if ((get().messagesById[sessionId] ?? []).length > 0) {
          return { ok: true, value: undefined };
        }
        const messages: ChatPanelMessage[] = transcript.messages.map((m, i) => ({
          id: `${sessionId}:${i}`,
          role: m.role,
          // Server messages don't carry a timestamp on this schema — mint one
          // so the bubble can label them. Stable per-index so re-renders match.
          timestamp: new Date(now() - (transcript.messages.length - i) * 1000).toISOString(),
          content: m.content,
          toolCalls: [],
        }));
        set((s) => ({
          ...s,
          messagesById: { ...s.messagesById, [sessionId]: messages },
        }));
        // Re-attach response-view diagram cards that were persisted on
        // assistant turns, so inline diagrams survive a transcript reload
        // instead of vanishing on refresh. The server records each rendered
        // view's id in `metadata.renderedViews`; rebuild the same envelope
        // the live `kb:viewRendered` handler creates (viewName falls back to
        // the id — the card fetches the real name/SVG via its load effect).
        transcript.messages.forEach((m, i) => {
          const viewIds = m.metadata?.renderedViews;
          if (!Array.isArray(viewIds) || viewIds.length === 0) return;
          const messageId = `${sessionId}:${i}`;
          for (const viewId of viewIds) {
            const envelope: InlineCardEnvelope = {
              kind: 'response-view-proposal',
              props: { viewId, viewName: viewId },
            };
            get().attachCardToMessage(messageId, envelope);
          }
        });
        // IV3-CARD — re-mount any LLM-raster ("illustrative-image") cards
        // persisted on assistant turns. These live in a DISTINCT
        // `metadata.images[]` field (keyed by `imageViewId`) so they rehydrate
        // as image cards, not diagram cards. For each record we (1) attach the
        // `illustrative-image` envelope to the producing message and (2) seed
        // the image-views slice (via the injected delegate) so the panel renders
        // the resolved raster + version history on the original turn.
        transcript.messages.forEach((m, i) => {
          const images = m.metadata?.images;
          if (!Array.isArray(images) || images.length === 0) return;
          const messageId = `${sessionId}:${i}`;
          for (const img of images) {
            const envelope: InlineCardEnvelope = {
              kind: 'illustrative-image',
              props: {
                imageViewId: img.imageViewId,
                viewId: img.viewId,
                ...(typeof img.sysmlSourcePath === 'string'
                  ? { sysmlSourcePath: img.sysmlSourcePath }
                  : {}),
                ...(typeof img.alt === 'string' ? { sceneDescriptionAlt: img.alt } : {}),
              },
            };
            get().attachCardToMessage(messageId, envelope);
            if (rehydrateImageFromManifest !== undefined) {
              rehydrateImageFromManifest({
                imageViewId: img.imageViewId,
                viewId: img.viewId,
                messageId,
                ...(typeof img.alt === 'string' ? { alt: img.alt } : {}),
              });
            }
          }
        });
        return { ok: true, value: undefined };
      },

      sendMessage: async (sessionId, body, mentions, opts) => {
        if (body.trim().length === 0) {
          return {
            ok: false,
            error: {
              code: 'INVALID_INPUT',
              message: 'message body must not be empty',
              retryable: false,
              requestId: 'client',
            },
          };
        }

        // Optimistic user message append.
        const userMsg: ChatPanelMessage = {
          id: genId(),
          role: 'user',
          timestamp: new Date(now()).toISOString(),
          content: body,
          toolCalls: [],
        };
        const pendingAssistantId = genId();

        set((s) => ({
          ...s,
          messagesById: {
            ...s.messagesById,
            [sessionId]: [...(s.messagesById[sessionId] ?? []), userMsg],
          },
          inFlightUserMessageCountBySession: {
            ...s.inFlightUserMessageCountBySession,
            [sessionId]: (s.inFlightUserMessageCountBySession[sessionId] ?? 0) + 1,
          },
          pendingAssistantMessageIdBySession: {
            ...s.pendingAssistantMessageIdBySession,
            [sessionId]: pendingAssistantId,
          },
          // Stamp the turn-start timestamp so the thinking indicator can
          // distinguish "no token for THIS turn yet" (show indicator) from
          // "leftover lastTokenAt from a previous turn" (would otherwise
          // suppress the indicator on every follow-up message).
          pendingStartedAtBySession: {
            ...s.pendingStartedAtBySession,
            [sessionId]: now(),
          },
        }));

        const res = await postChat(
          {
            sessionId,
            messageBody: body,
            mentions: mentions?.map((m) => ({ chipText: m.chipText, entityId: m.entityId })),
            ...(opts?.layoutAdjust !== undefined ? { layoutAdjust: opts.layoutAdjust } : {}),
          },
          ctx,
        );

        if (!res.ok) {
          // Roll back the in-flight counter; the user message stays so the
          // user can try again. Pending assistant id cleared so thinking
          // indicator hides.
          set((s) => ({
            ...s,
            inFlightUserMessageCountBySession: {
              ...s.inFlightUserMessageCountBySession,
              [sessionId]: Math.max(
                0,
                (s.inFlightUserMessageCountBySession[sessionId] ?? 1) - 1,
              ),
            },
            pendingAssistantMessageIdBySession: {
              ...s.pendingAssistantMessageIdBySession,
              [sessionId]: null,
            },
            pendingStartedAtBySession: {
              ...s.pendingStartedAtBySession,
              [sessionId]: 0,
            },
          }));
          return res;
        }
        return { ok: true, value: { messageId: res.value.messageId } };
      },

      appendToken: ({ messageId, text, sessionId }) => {
        // Locate session — caller may pass it explicitly; otherwise scan.
        let target = sessionId ?? null;
        if (target === null) {
          const found = findMessageById(messageId);
          if (found !== null) target = found[0];
        }
        // First-token fallback: create the assistant bubble in the active
        // session if one is set.
        target ??= get().activeSessionId;
        if (target === null) return;

        ensureAssistantMessage(target, messageId);
        const ts = now();
        set((s) => {
          const list = s.messagesById[target] ?? [];
          const idx = list.findIndex((m) => m.id === messageId);
          if (idx < 0) return s;
          const updated = [...list];
          const existing = updated[idx];
          if (existing === undefined) return s;
          updated[idx] = { ...existing, content: existing.content + text };
          return {
            ...s,
            messagesById: { ...s.messagesById, [target]: updated },
            streamingMessageIdBySession: {
              ...s.streamingMessageIdBySession,
              [target]: messageId,
            },
            lastTokenAtBySession: { ...s.lastTokenAtBySession, [target]: ts },
          };
        });
      },

      recordToolCall: ({ messageId, toolName, input, callId, sessionId }) => {
        let target = sessionId ?? null;
        if (target === null) {
          const found = findMessageById(messageId);
          if (found !== null) target = found[0];
        }
        target ??= get().activeSessionId;
        if (target === null) return;

        ensureAssistantMessage(target, messageId);

        const ts = now();
        const entry: ToolCallEntry = {
          callId,
          toolName,
          input,
          status: 'running',
          startedAt: new Date(ts).toISOString(),
        };

        set((s) => {
          const list = s.messagesById[target] ?? [];
          const idx = list.findIndex((m) => m.id === messageId);
          if (idx < 0) return s;
          const updated = [...list];
          const existing = updated[idx];
          if (existing === undefined) return s;
          updated[idx] = {
            ...existing,
            toolCalls: [...(existing.toolCalls ?? []), entry],
          };
          return {
            ...s,
            messagesById: { ...s.messagesById, [target]: updated },
            lastToolCallAtBySession: { ...s.lastToolCallAtBySession, [target]: ts },
          };
        });
      },

      recordToolResult: ({ callId, output, status, errorMessage }) => {
        const found = findCallById(callId);
        if (found === null) return;
        const ts = now();
        set((s) => {
          const list = s.messagesById[found.sessionId] ?? [];
          const msg = list[found.messageIdx];
          if (msg === undefined) return s;
          const calls = [...(msg.toolCalls ?? [])];
          const existing = calls[found.callIdx];
          if (existing === undefined) return s;
          calls[found.callIdx] = {
            ...existing,
            output,
            status,
            errorMessage,
            completedAt: new Date(ts).toISOString(),
          };
          const updated = [...list];
          updated[found.messageIdx] = { ...msg, toolCalls: calls };
          return {
            ...s,
            messagesById: { ...s.messagesById, [found.sessionId]: updated },
            lastToolResultAtBySession: {
              ...s.lastToolResultAtBySession,
              [found.sessionId]: ts,
            },
          };
        });
      },

      appendSystemMessage: (sessionId, content) => {
        const sys: ChatPanelMessage = {
          id: genId(),
          role: 'system',
          timestamp: new Date(now()).toISOString(),
          content,
          toolCalls: [],
        };
        set((s) => ({
          ...s,
          messagesById: {
            ...s.messagesById,
            [sessionId]: [...(s.messagesById[sessionId] ?? []), sys],
          },
        }));
      },

      appendSystemNotice: (content) => {
        // W2-S6-integration — narrow alias for the active session. Used by
        // `<ResponseViewProposalCard>`'s `onAppendDiscardNotice` to post the
        // CLAUDE.md exact phrasing `"You discarded the generated view."`
        // without the caller having to thread the active session id through
        // the inline-card handler.
        const sid = get().activeSessionId;
        if (sid === null) {
          // Defensive fallback — SSE bootstrap can race the session-active
          // setter; warn instead of throwing so the rest of the dispatch
          // chain stays alive.
          console.warn(
            'chat.appendSystemNotice: no active session, dropping notice',
            { content },
          );
          return;
        }
        get().appendSystemMessage(sid, content);
      },

      attachCardToMessage: (messageId, envelope) => {
        if (attachInlineCardDelegate === undefined) {
          // Standalone test path — no inline-cards slice composed. The
          // chat-slice unit tests don't exercise card attachment; the
          // integration tests run against the root store and inject the
          // delegate via `state/store.ts`.
          console.warn(
            'chat.attachCardToMessage: no inline-cards delegate wired, dropping envelope',
            { messageId, kind: envelope.kind },
          );
          return -1;
        }
        return attachInlineCardDelegate(messageId, envelope);
      },

      finishStream: (sessionId) => {
        set((s) => ({
          ...s,
          streamingMessageIdBySession: {
            ...s.streamingMessageIdBySession,
            [sessionId]: null,
          },
          pendingAssistantMessageIdBySession: {
            ...s.pendingAssistantMessageIdBySession,
            [sessionId]: null,
          },
          pendingStartedAtBySession: {
            ...s.pendingStartedAtBySession,
            [sessionId]: 0,
          },
          inFlightUserMessageCountBySession: {
            ...s.inFlightUserMessageCountBySession,
            [sessionId]: Math.max(
              0,
              (s.inFlightUserMessageCountBySession[sessionId] ?? 1) - 1,
            ),
          },
        }));
      },

      setSessionTitle: (sessionId, title) => {
        set((s) => ({
          ...s,
          sessions: s.sessions.map((m) => (m.id === sessionId ? { ...m, title } : m)),
        }));
        // TODO(W2-S8): PUT /api/v1/chat/sessions/:sessionId — server route
        // not yet defined for rename; orchestrator owns the persistence path.
      },

      setSessionMeta: (sessionId, partial) => {
        set((s) => {
          const existing = s.sessions.find((m) => m.id === sessionId);
          if (existing === undefined) {
            const meta: SessionMeta = {
              id: sessionId,
              title: partial.title ?? '',
              providerName: partial.providerName ?? 'unknown',
              tokensUsed: partial.tokensUsed ?? null,
              capabilities: partial.capabilities ?? emptyCapabilities(),
              lastModified: partial.lastModified ?? new Date(now()).toISOString(),
            };
            return { ...s, sessions: [meta, ...s.sessions] };
          }
          return {
            ...s,
            sessions: s.sessions.map((m) =>
              m.id === sessionId ? { ...m, ...partial } : m,
            ),
          };
        });
      },

      regenerateLast: async (sessionId) => {
        // Wiring §8 polish — re-dispatch the last user message through the
        // already-wired `sendMessage` path. `sendMessage` owns the optimistic
        // user-message append + the POST to `/api/v1/chat/sessions/:id/messages`
        // which the orchestrator picks up server-side (the cheat-sheet's
        // Ctrl+R / `/regenerate` binding lands here). The earlier
        // `console.warn` TODO predated `sendMessage` being plumbed through.
        const list = get().messagesById[sessionId] ?? [];
        const last = [...list].reverse().find((m) => m.role === 'user');
        if (last === undefined) return;
        await get().sendMessage(sessionId, last.content);
      },

      clearMessages: async (sessionId) => {
        // Optimistic local clear so the UI updates immediately. If the
        // server call fails, the next `loadTranscript` (e.g. on tab return)
        // rehydrates from disk.
        set((s) => ({
          ...s,
          messagesById: { ...s.messagesById, [sessionId]: [] },
          streamingMessageIdBySession: {
            ...s.streamingMessageIdBySession,
            [sessionId]: null,
          },
          pendingAssistantMessageIdBySession: {
            ...s.pendingAssistantMessageIdBySession,
            [sessionId]: null,
          },
          sessions: s.sessions.map((m) =>
            m.id === sessionId ? { ...m, tokensUsed: null } : m,
          ),
        }));
        const res = await sessionsApi.clearMessages(ctx, sessionId);
        if (!res.ok) {
          console.warn('chat.clearMessages: server clear failed', {
            sessionId,
            error: res.error,
          });
        }
      },

      deleteMessage: async (sessionId, messageId) => {
        const list = get().messagesById[sessionId] ?? [];
        const index = list.findIndex((m) => m.id === messageId);
        if (index < 0) return;
        // Deletion is positional, but the local list and the on-disk
        // `transcript.messages` array only line up 1:1 immediately after
        // loadTranscript. A multi-message turn can persist MORE messages than
        // the SPA rendered as bubbles (e.g. a system-ack + a final assistant
        // message collapse into one visible bubble), pushing the intended
        // message to a higher server index than its local index — so the bare
        // index would splice out the WRONG message. Send the target's identity
        // (role + content) so the server verifies the hinted index and, on
        // drift, re-locates the exact message before splicing.
        const target = list[index];
        const res = await sessionsApi.deleteMessage(
          ctx,
          sessionId,
          index,
          target !== undefined
            ? { expectedRole: target.role, expectedContent: target.content }
            : undefined,
        );
        if (!res.ok) {
          // The server rejects the delete when the local list has drifted past
          // the on-disk transcript with no matching message: an optimistic
          // bubble a failed `sendMessage` left behind (that path intentionally
          // KEEPS the user message so it can be retried, but the server never
          // persisted it) comes back 404 NOT_FOUND (identity search found no
          // match) or 400 VALIDATION (legacy index-only, out of range); a
          // session never created server-side also 404s. In every such case the
          // message is a client-only artifact the user explicitly asked to
          // remove, so drop it locally instead of stranding an un-deletable
          // bubble (the reported "can't close the last chat message" bug).
          // Transient/auth failures (retryable 5xx, 401/403) are left intact and
          // warned so a genuinely persisted message isn't hidden then resurrected
          // on the next transcript reload.
          const orphanNotOnServer =
            res.error.code === 'VALIDATION' || res.error.code === 'NOT_FOUND';
          if (!orphanNotOnServer) {
            console.warn('chat.deleteMessage: server delete failed', {
              sessionId,
              messageId,
              error: res.error,
            });
            return;
          }
        }
        set((s) => {
          const current = s.messagesById[sessionId] ?? [];
          const idx = current.findIndex((m) => m.id === messageId);
          if (idx < 0) return s;
          const updated = [...current.slice(0, idx), ...current.slice(idx + 1)];
          return {
            ...s,
            messagesById: { ...s.messagesById, [sessionId]: updated },
          };
        });
      },

      deleteSession: async (sessionId) => {
        const res = await sessionsApi.deleteSession(ctx, sessionId);
        if (!res.ok) return res;
        set((s) => {
          const messagesById = { ...s.messagesById };
          delete messagesById[sessionId];
          return {
            ...s,
            sessions: s.sessions.filter((m) => m.id !== sessionId),
            messagesById,
          };
        });
        return { ok: true, value: undefined };
      },

      exportTranscript: (sessionId, format) => {
        const list = get().messagesById[sessionId] ?? [];
        if (format === 'json') {
          return JSON.stringify(list, null, 2);
        }
        // Markdown render — minimal but reproducible.
        const lines: string[] = [];
        for (const m of list) {
          lines.push(`### ${m.role} · ${m.timestamp}`);
          lines.push('');
          lines.push(m.content);
          lines.push('');
        }
        return lines.join('\n');
      },

      retryToolCall: (_callId) => {
        console.warn('TODO(W1-S3a): wire tool retry');
      },

      rerunToolCall: (_callId) => {
        console.warn('TODO(W1-S3a): wire tool re-run');
      },

      _resetChat: () => {
        set({ ...CHAT_INITIAL_STATE } as ChatSlice);
      },
    };
  };

/** Vanilla store factory for tests / non-shell call-sites. */
export function createChatStore(config: ChatSliceConfig = {}) {
  return createStore<ChatSlice>()(createChatSlice(config));
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectSessionMeta(sessionId: string) {
  return (s: ChatSliceState): SessionMeta | null =>
    s.sessions.find((m) => m.id === sessionId) ?? null;
}

export function selectMessages(sessionId: string) {
  return (s: ChatSliceState): ChatPanelMessage[] => s.messagesById[sessionId] ?? [];
}

export function selectStreamingMessageId(sessionId: string) {
  return (s: ChatSliceState): string | null =>
    s.streamingMessageIdBySession[sessionId] ?? null;
}

export function selectPendingStartedAt(sessionId: string) {
  return (s: ChatSliceState): number =>
    s.pendingStartedAtBySession[sessionId] ?? 0;
}

export function selectPendingAssistantMessageId(sessionId: string) {
  return (s: ChatSliceState): string | null =>
    s.pendingAssistantMessageIdBySession[sessionId] ?? null;
}

export function selectInFlightUserMessageCount(sessionId: string) {
  return (s: ChatSliceState): number =>
    s.inFlightUserMessageCountBySession[sessionId] ?? 0;
}
